import i18next from 'i18next';
import Backend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

import logger from './services/logger';
import { logEvents, logLevels } from './constants/appConstants';
import { errorHandlerForClass } from './services/errorHandler';
import {
    getCachedTranslation,
    cacheTranslation,
} from './services/translationCache';

// Get global config settings, or use an empty one if not found.
const config = typeof _CONFIG_ !== 'undefined' ? _CONFIG_ : {};
// Unique hash for fresh translations, usually from our build process.
const CACHING_HASH = process.env.CACHING_HASH || 'default_hash';

/**
 * Sets up i18next for translations across the app.
 * @param {string} lang The language to load (e.g., 'en', 'pt-br').
 * @param {boolean} debugMode Turn on extra logs for debugging translation issues.
 * @param {function} navigate (Optional) Helps us redirect if translation loading fails.
 * @param {number} timeout Optional timeout in milliseconds for translation loading.
 */
export const initI18n = async(lang, debugMode, navigate, timeout = 8000) => {
    // Figure out the base URL for our translation files.
    const cdnBase = config.cdn || '';

    // Build the full path to the specific language JSON file.
    const languageURL = `${cdnBase}lang/${CACHING_HASH}-${lang}.json`;

    // Log the URL we're trying to fetch, good for checking.
    logger(logLevels.DEBUG, {
        source: 'i18n.js Init',
        action: logEvents.EXTERNAL_EVENT_CALLED,
        payload: { url: languageURL },
    });

    try {
    // OPTIMIZATION: Try to get cached translation first
        let translationData = null;
        try {
            translationData = await getCachedTranslation(languageURL);
            if (translationData) {
                logger(logLevels.DEBUG, {
                    source: 'i18n.js Init',
                    action: logEvents.CACHE_HIT,
                    payload: { message: 'Using cached translation', url: languageURL },
                });
            }
        } catch (cacheError) {
            logger(logLevels.DEBUG, {
                source: 'i18n.js Init',
                action: logEvents.UNHANDLED_ERROR,
                payload: {
                    message: 'Cache lookup failed, will fetch from network',
                    error: cacheError.message,
                },
            });
        }

        // Create a promise that rejects after the timeout
        const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => {
                reject(new Error(`i18next initialization timeout after ${timeout}ms`));
            }, timeout);
        });

        // Create a custom backend that uses cache
        const backendConfig = {
            loadPath: languageURL,
            crossDomain: true,
            requestOptions: {
                timeout: timeout - 500, // Leave buffer for processing
            },
            // OPTIMIZATION: Add custom request handler for caching
            request: async function request(options, url, payload, callback) {
                try {
                    // First, check cache again (might have been updated)
                    const cached = await getCachedTranslation(url);
                    if (cached) {
                        return callback(null, {
                            status: 200,
                            data: cached,
                        });
                    }

                    // Fetch from network
                    const response = await fetch(url, {
                        method: 'GET',
                        cache: 'default',
                        credentials: 'same-origin',
                    });

                    if (!response.ok) {
                        return callback(null, {
                            status: response.status,
                            statusText: response.statusText,
                        });
                    }

                    const data = await response.json();

                    // Cache the result asynchronously (don't await, fire and forget)
                    cacheTranslation(url, data).catch((error) => {
                        logger(logLevels.DEBUG, {
                            source: 'i18n.js Init',
                            action: logEvents.UNHANDLED_ERROR,
                            payload: {
                                message: 'Failed to cache translation',
                                error: error.message,
                            },
                        });
                    });

                    return callback(null, {
                        status: 200,
                        data: data,
                    });
                } catch (error) {
                    return callback(error);
                }
            },
        };

        // Race between i18next init and timeout
        await Promise.race([
            i18next
            // Use the HTTP backend to fetch JSON translation files.
                .use(Backend)
            // Connects i18next to React components.
                .use(initReactI18next)
                .init({
                    lng: lang, // Set the initial language.
                    // matching our file names, instead of trying to simplify to 'pt'.
                    // load: 'languageOnly', // <-- No longer needed!
                    fallbackLng: 'en', // If a translation isn't found, default to English.
                    debug: debugMode, // Show i18next's internal logs if debugging is on.
                    interpolation: {
                        prefix: '__',
                        suffix: '__',
                        escapeValue: false, // Don't mess with HTML in translations.
                    },
                    backend: backendConfig,
                    nsSeparator: false, // Not using namespaces, so this is off.
                }),
            timeoutPromise,
        ]);

        logger(logLevels.DEBUG, {
            source: 'i18n.js Init',
            action: logEvents.COMPONENT_MOUNTED,
            payload: 'i18next initialized successfully',
        });
    } catch (error) {
        logger(logLevels.ERROR, {
            source: 'i18n.js Init Error',
            action: logEvents.UNHANDLED_ERROR,
            payload: {
                error: error,
                message: 'i18next initialization failed',
                stack: error.stack,
            },
        });

        // Optionally, use your error handler if you want to navigate on i18n load failure
        if (navigate) {
            errorHandlerForClass(
                {
                    Message: 'Network error initializing translations',
                    Exception:
            error instanceof Error
                ? error.toString()
                : `${JSON.stringify(error)}`,
                },
                debugMode,
                navigate,
            );
        }
    }
};

export default i18next;
